import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Dialog, Transition, TransitionChild } from "@headlessui/react";
import { Link } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useLocale } from "../../contexts/LocaleContext";
import { searchCasWebsite } from "../../services/search";
import { normalizeMenuUrl } from "../../utils/menuUrl";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

const SearchResultItem = ({ hit, onNavigate }) => {
  const url = normalizeMenuUrl(hit.url);
  const title = hit.title || "Untitled";
  const pageLabel = hit.pageLabel || "Content";

  const content = (
    <>
      <p className="font-medium text-gray-900 group-hover:text-lake-600 transition-colors line-clamp-2">
        {title}
      </p>
      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-lake-600/80">
        {pageLabel}
      </p>
    </>
  );

  const className =
    "group block rounded-lg border border-mist-200/80 bg-white px-4 py-3 transition-colors hover:border-lake-300 hover:bg-lake-50/40";

  if (!url) {
    return <div className={className}>{content}</div>;
  }

  if (/^https?:\/\//i.test(url)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={onNavigate}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={url} className={className} onClick={onNavigate}>
      {content}
    </Link>
  );
};

const SearchModal = ({ isOpen, onClose }) => {
  const { locale } = useLocale();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handle = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [query, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setDebouncedQuery("");
      setResults([]);
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const runSearch = useCallback(async () => {
    if (debouncedQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const hits = await searchCasWebsite(debouncedQuery, locale);
      setResults(hits);
    } catch (err) {
      setResults([]);
      setError(err?.message || "Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, locale]);

  useEffect(() => {
    if (!isOpen) return;
    runSearch();
  }, [isOpen, runSearch]);

  const handleNavigate = () => {
    onClose();
  };

  const showHint =
    query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH;
  const showEmpty =
    !loading &&
    !error &&
    debouncedQuery.length >= MIN_QUERY_LENGTH &&
    results.length === 0;

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-[60]">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-start justify-center p-4 pt-20 sm:pt-24">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-2 scale-[0.98]"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-[0.98]"
          >
            <Dialog.Panel className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
              <div className="flex items-center gap-3 border-b border-mist-200/80 px-4 py-3 sm:px-5">
                <MagnifyingGlassIcon
                  className="h-5 w-5 shrink-0 text-lake-500"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search the website..."
                  autoFocus
                  autoComplete="off"
                  className="min-w-0 flex-1 border-0 bg-transparent text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                />
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-2 text-gray-500 hover:bg-mist-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-lake-500"
                  aria-label="Close search"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-[min(60vh,28rem)] overflow-y-auto px-4 py-4 sm:px-5">
                {showHint && (
                  <p className="text-sm text-gray-500">
                    Type at least {MIN_QUERY_LENGTH} characters to search.
                  </p>
                )}

                {loading && (
                  <p className="text-sm text-gray-500">Searching...</p>
                )}

                {error && (
                  <p className="text-sm text-red-600" role="alert">
                    {error}
                  </p>
                )}

                {showEmpty && (
                  <p className="text-sm text-gray-500">
                    No results found for &ldquo;{debouncedQuery}&rdquo;.
                  </p>
                )}

                {!loading && !error && results.length > 0 && (
                  <div className="space-y-2">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {results.length} result{results.length === 1 ? "" : "s"}
                    </p>
                    {results.map((hit) => (
                      <SearchResultItem
                        key={hit.id || `${hit.documentId}-${hit.url}`}
                        hit={hit}
                        onNavigate={handleNavigate}
                      />
                    ))}
                  </div>
                )}

                {!query.trim() && !loading && (
                  <p className="text-sm text-gray-500">
                    Search products, news, downloads, and more.
                  </p>
                )}
              </div>
            </Dialog.Panel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SearchModal;
